import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Send, Square, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  blobToDataUrl,
  downscaleImageFile,
  formatRecordingTime,
  getSupportedAudioMimeType,
  readFileAsDataUrl,
  readVideoMetadata,
} from './messengerMediaUtils';

const iconColor = 'var(--text-2)';

const CameraActionIcon = () => (
  <svg width="25" height="18" viewBox="0 0 25 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.6942 6.59212C10.272 6.59212 8.30163 8.47649 8.30163 10.7926C8.30163 13.1088 10.2721 14.9932 12.6942 14.9932C15.1162 14.9932 17.0867 13.1091 17.0867 10.7926C17.0867 8.47616 15.1162 6.59212 12.6942 6.59212ZM8.67925 5.73749C9.77681 4.94794 11.1404 4.47916 12.6166 4.47916C14.0927 4.47916 15.4563 4.94783 16.5538 5.73749H25V4.8483C25 3.62229 23.951 2.61919 22.669 2.61919H16.9899C16.9892 2.52022 16.9679 2.41412 16.922 2.30311L16.3484 0.92909C16.1351 0.418068 15.4897 0 14.9139 0H10.0854C9.51002 0 8.86469 0.418179 8.65128 0.92909L8.07751 2.30311C8.03112 2.41412 8.01014 2.52022 8.00956 2.61919H6.84732V2.28483C6.84732 1.91703 6.53263 1.6161 6.14802 1.6161H3.87529C3.49068 1.6161 3.17599 1.91703 3.17599 2.28483V2.61919H2.331C1.04895 2.61919 0 3.62229 0 4.8483V5.73749H8.67925ZM17.7502 6.85204C18.6438 7.92446 19.1798 9.28087 19.1798 10.7554C19.1798 14.2161 16.2354 17.0318 12.6166 17.0318C8.99767 17.0318 6.05338 14.2161 6.05338 10.7554C6.05338 9.28087 6.58928 7.92446 7.48275 6.85204H0V15.7709C0 16.9969 1.04895 18 2.331 18H22.669C23.951 18 25 16.9969 25 15.7709V6.85204H17.7502Z" fill={iconColor}/>
  </svg>
);

const ImageActionIcon = () => (
  <svg width="25" height="19" viewBox="0 0 25 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M17.7606 3.51397C18.2174 3.51397 18.6639 3.65254 19.0436 3.91217C19.4234 4.17179 19.7194 4.54081 19.8942 4.97255C20.069 5.40429 20.1147 5.87936 20.0256 6.33769C19.9365 6.79603 19.7166 7.21703 19.3936 7.54747C19.0706 7.87791 18.6591 8.10294 18.2112 8.19411C17.7632 8.28528 17.2989 8.23849 16.8769 8.05965C16.4549 7.88082 16.0942 7.57798 15.8405 7.18942C15.5867 6.80087 15.4513 6.34405 15.4513 5.87674C15.4513 5.56646 15.511 5.25921 15.6271 4.97255C15.7431 4.68588 15.9132 4.42541 16.1277 4.20601C16.3421 3.98661 16.5967 3.81257 16.8769 3.69382C17.1571 3.57508 17.4574 3.51397 17.7606 3.51397ZM23.3214 19H1.68064C1.2351 18.998 0.808509 18.8154 0.494425 18.4921C0.338049 18.333 0.213885 18.1439 0.129043 17.9356C0.0442018 17.7274 0.000350138 17.504 0 17.2784L0 1.71951C7.57621e-05 1.2635 0.177192 0.826192 0.49239 0.50378C0.807513 0.181291 1.23493 7.75146e-05 1.68064 0L23.3214 0C23.7667 0.00118102 24.1933 0.183119 24.5076 0.505862C24.8228 0.828273 24.9999 1.26558 25 1.7216V17.2784C24.9999 17.5039 24.9564 17.7271 24.8719 17.9354C24.7874 18.1436 24.6636 18.3328 24.5076 18.4921C24.1935 18.8154 23.7669 18.998 23.3214 19ZM21.8259 17.0369L17.8237 10.5044C17.7409 10.3698 17.6261 10.2589 17.49 10.182C17.3538 10.1052 17.2009 10.0648 17.0455 10.0648C16.89 10.0648 16.7371 10.1052 16.601 10.182C16.4649 10.2589 16.35 10.3698 16.2672 10.5044L14.377 13.6125L16.434 17.0369H15.5896L9.98006 7.87729C9.46325 7.17158 8.63514 7.2257 8.17327 7.87729L2.67966 17.0369H1.91666V1.96099H23.0772V17.0369H21.8259Z" fill={iconColor}/>
  </svg>
);

const MicActionIcon = () => (
  <svg width="20" height="25" viewBox="0 0 20 25" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M18.75 11.25C19.4404 11.25 20 11.8096 20 12.5C20 17.5991 16.1835 21.8068 11.2512 22.4225L11.25 23.75C11.25 24.4404 10.6904 25 10 25C9.30964 25 8.75 24.4404 8.75 23.75L8.75006 22.4226C3.81714 21.8075 0 17.5995 0 12.5C0 11.8096 0.559644 11.25 1.25 11.25C1.94036 11.25 2.5 11.8096 2.5 12.5C2.5 16.6421 5.85786 20 10 20C14.1421 20 17.5 16.6421 17.5 12.5C17.5 11.8096 18.0596 11.25 18.75 11.25ZM10 0C12.7614 0 15 2.23858 15 5V12.5C15 15.2614 12.7614 17.5 10 17.5C7.23858 17.5 5 15.2614 5 12.5V5C5 2.23858 7.23858 0 10 0ZM10 2.5C8.61929 2.5 7.5 3.61929 7.5 5V12.5C7.5 13.8807 8.61929 15 10 15C11.3807 15 12.5 13.8807 12.5 12.5V5C12.5 3.61929 11.3807 2.5 10 2.5Z" fill={iconColor}/>
  </svg>
);

const actionAnimation = {
  type: 'spring',
  stiffness: 420,
  damping: 32,
};

export const MessengerComposer = ({
  value,
  onChange,
  placeholder = 'Message',
  dataTestIdPrefix,
  onSendText,
  onSendImage,
  onSendVideo,
  onSendAudio,
  replyingTo,
  onCancelReply,
  editingMessage = null,
  onSaveEdit,
  onCancelEdit,
}) => {
  const textareaRef = useRef(null);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingStartedAtRef = useRef(0);
  // Flag read inside `recorder.onstop` so a cancel tap discards the clip
  // instead of sending it. We cannot destroy the recorder synchronously —
  // onstop still fires after `.stop()` — so we flip this flag first.
  const recordingCancelledRef = useRef(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Mirror the live textarea.value separately from the parent React
  // state so Greek / Chinese / Japanese IME compositions (where
  // suggestion taps commit text via `beforeinput` without firing a
  // React-visible `input`) still flip the Send button on and hide the
  // placeholder. Without this: typing "Γλυφαδ" and tapping the Greek
  // keyboard suggestion for "Γλυφάδα" leaves React state at ""
  // (or at the partial) — the send bubble never appears and the
  // "Message" placeholder stays painted on top of the composed text.
  const [liveValue, setLiveValue] = useState(value);
  useEffect(() => { setLiveValue(value); }, [value]);
  const hasTypedText = Boolean((liveValue || '').trim());

  // Native DOM input listener — React's synthetic `onChange` /
  // `onInput` is SUPPRESSED during an active IME composition on
  // Android (Greek, Chinese, Japanese). Without this bypass the
  // Send button never appears while composing "Γλυφαδ" because
  // React never tells us the DOM value changed. A plain native
  // listener fires on every single character stroke, composition
  // or not. We only drive `liveValue` so the Send button flips —
  // the parent's `value` is still updated by onCompositionEnd /
  // onChange, exactly as before.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return undefined;
    const sync = () => setLiveValue(textarea.value || '');
    textarea.addEventListener('input', sync);
    textarea.addEventListener('compositionupdate', sync);
    textarea.addEventListener('compositionend', sync);
    textarea.addEventListener('beforeinput', sync);
    return () => {
      textarea.removeEventListener('input', sync);
      textarea.removeEventListener('compositionupdate', sync);
      textarea.removeEventListener('compositionend', sync);
      textarea.removeEventListener('beforeinput', sync);
    };
  }, []);

  const syncTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = '0px';
    const lineHeight = Number.parseFloat(window.getComputedStyle(textarea).lineHeight) || 22;
    const maxHeight = lineHeight * 3;
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  const stopMediaTracks = useCallback(() => {
    mediaStreamRef.current?.getTracks()?.forEach((track) => track.stop());
    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;
  }, []);

  useEffect(() => {
    syncTextareaHeight();
  }, [syncTextareaHeight, value]);

  useEffect(() => {
    if (!isRecording) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setRecordingSeconds(Math.floor((Date.now() - recordingStartedAtRef.current) / 1000));
    }, 250);

    return () => window.clearInterval(intervalId);
  }, [isRecording]);

  useEffect(() => () => stopMediaTracks(), [stopMediaTracks]);

  const runWithPendingState = useCallback(async (callback) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      await callback();
    } catch (error) {
      return;
    } finally {
      // One last synchronous refocus right before React re-renders the
      // button area — closes the final tiny window where Android could
      // interpret the brief blur as a keyboard-dismiss trigger. Belt
      // and suspenders alongside the pre-submit focus in handleTextSubmit.
      try { textareaRef.current?.focus({ preventScroll: true }); } catch { /* noop */ }
      setIsSubmitting(false);
    }
  }, [isSubmitting]);

  const handleTextSubmit = async () => {
    // Read directly from the DOM so IME-committed characters (Greek
    // autocomplete, Chinese pinyin, etc.) that haven't flushed to
    // React state yet still get sent in full. Without this, tapping
    // the Greek keyboard suggestion for "Γλυφάδα" and immediately
    // tapping Send submits the partial "Γλυφαδ" that React state
    // last saw.
    const latestValue = textareaRef.current?.value ?? value;
    const text = (latestValue || '').trim();
    if (!text) {
      return;
    }

    // CRITICAL: refocus SYNCHRONOUSLY before any async / state work so
    // Android's soft keyboard never gets a window to dismiss. The old
    // code refocused via requestAnimationFrame AFTER the send resolved
    // — on native Capacitor WebView the keyboard had already collapsed
    // by then, producing the "closes and reopens" flicker users saw.
    try { textareaRef.current?.focus({ preventScroll: true }); } catch { /* noop */ }

    if (editingMessage && onSaveEdit) {
      // Editing an existing message — only fire when text actually changed.
      const originalText = (editingMessage.content || '').trim();
      if (text === originalText) {
        onCancelEdit?.();
        return;
      }
      await runWithPendingState(async () => {
        await onSaveEdit(editingMessage, text);
      });
      // Belt-and-suspenders: re-focus after the final re-render in case
      // AnimatePresence unmounting the Send button briefly stole focus.
      requestAnimationFrame(() => {
        try { textareaRef.current?.focus({ preventScroll: true }); } catch { /* noop */ }
      });
      return;
    }

    if (!onSendText) {
      return;
    }
    await runWithPendingState(async () => {
      await onSendText(text);
    });
    requestAnimationFrame(() => {
      try { textareaRef.current?.focus({ preventScroll: true }); } catch { /* noop */ }
    });
  };

  const handleImageSelection = async (event, source) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    const isVideo = (file.type || '').startsWith('video/');
    if (isVideo) {
      if (!onSendVideo) {
        toast.error('Video is not supported here.');
        return;
      }
      // Guardrail: 25MB cap keeps data URLs sane on mobile.
      if (file.size > 25 * 1024 * 1024) {
        toast.error('Videos must be under 25MB.');
        return;
      }
      await runWithPendingState(async () => {
        const dataUrl = await readFileAsDataUrl(file);
        const meta = await readVideoMetadata(dataUrl);
        await onSendVideo({
          data_url: dataUrl,
          mime_type: file.type || 'video/mp4',
          file_name: file.name,
          width: meta.width,
          height: meta.height,
          duration_seconds: meta.duration,
          source,
        });
      });
      return;
    }

    if (!onSendImage) {
      return;
    }

    await runWithPendingState(async () => {
      const imagePayload = await downscaleImageFile(file);
      await onSendImage({
        data_url: imagePayload.dataUrl,
        mime_type: imagePayload.mimeType,
        file_name: file.name,
        width: imagePayload.width,
        height: imagePayload.height,
        source,
      });
    });
  };

  const startRecording = async () => {
    if (!onSendAudio) {
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Voice recording is not supported on this device.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedAudioMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (recordingEvent) => {
        if (recordingEvent.data?.size) {
          audioChunksRef.current.push(recordingEvent.data);
        }
      };

      recorder.onstop = async () => {
        const wasCancelled = recordingCancelledRef.current;
        const audioBlob = new Blob(audioChunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
        stopMediaTracks();
        audioChunksRef.current = [];
        setRecordingSeconds(0);
        setIsRecording(false);

        if (wasCancelled) {
          // Discard the recording — user tapped the trash button.
          recordingCancelledRef.current = false;
          return;
        }

        await runWithPendingState(async () => {
          const dataUrl = await blobToDataUrl(audioBlob);
          await onSendAudio({
            data_url: dataUrl,
            mime_type: audioBlob.type || recorder.mimeType || mimeType || 'audio/webm',
            duration_seconds: Math.max(1, Math.floor((Date.now() - recordingStartedAtRef.current) / 1000)),
          });
        });
      };

      recorder.start();
      recordingStartedAtRef.current = Date.now();
      recordingCancelledRef.current = false;
      setRecordingSeconds(0);
      setIsRecording(true);
    } catch (error) {
      toast.error('Microphone permission is required to send a voice message.');
    }
  };

  const handleMicToggle = async () => {
    if (isSubmitting) {
      return;
    }

    if (isRecording) {
      mediaRecorderRef.current?.stop();
      return;
    }

    await startRecording();
  };

  const handleCancelRecording = () => {
    // Flip the cancel flag BEFORE calling stop so `recorder.onstop`
    // discards the chunks instead of posting the audio message.
    if (!isRecording) return;
    recordingCancelledRef.current = true;
    try {
      mediaRecorderRef.current?.stop();
    } catch {
      // If stop throws (already-inactive recorder), make sure we still
      // clean up the UI + media tracks so the user isn't stuck.
      stopMediaTracks();
      audioChunksRef.current = [];
      setRecordingSeconds(0);
      setIsRecording(false);
      recordingCancelledRef.current = false;
    }
  };

  return (
    <div
      data-testid={`${dataTestIdPrefix}-composer`}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        flexDirection: 'column',
        gap: 10,
        minHeight: 58,
        padding: '8px 12px',
        borderRadius: 26,
        background: 'var(--surface)',
        border: '1px solid var(--card-border)',
        boxShadow: 'var(--premium-shadow-nested)',
      }}
    >
      {editingMessage ? (
        <div data-testid={`${dataTestIdPrefix}-editing-bar`} style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, borderRadius: 16, background: 'rgba(112, 63, 246, 0.10)', border: '1px solid rgba(112, 63, 246, 0.28)', padding: '9px 10px' }}>
          <div style={{ width: 3, borderRadius: 999, background: 'var(--accent-purple)', alignSelf: 'stretch' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 13, fontWeight: 700, color: 'var(--accent-purple-strong, #6335e0)' }}>Editing message</p>
            <p style={{ margin: '2px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{editingMessage.content || ''}</p>
          </div>
          <button type="button" data-testid={`${dataTestIdPrefix}-editing-cancel-button`} onClick={onCancelEdit} style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
            <X size={14} color="var(--text-2)" />
          </button>
        </div>
      ) : null}

      {replyingTo ? (
        <div data-testid={`${dataTestIdPrefix}-replying-bar`} style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, borderRadius: 16, background: 'var(--surface-2)', border: '1px solid var(--card-border)', padding: '9px 10px' }}>
          <div style={{ width: 3, borderRadius: 999, background: 'var(--accent-purple)', alignSelf: 'stretch' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Replying to {replyingTo.sender_name}</p>
            <p style={{ margin: '2px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{replyingTo.preview_text}</p>
          </div>
          <button type="button" data-testid={`${dataTestIdPrefix}-replying-cancel-button`} onClick={onCancelReply} style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
            <X size={14} color="var(--text-2)" />
          </button>
        </div>
      ) : null}

      <input ref={cameraInputRef} type="file" accept="image/*,video/*" capture="environment" data-testid={`${dataTestIdPrefix}-camera-input`} style={{ display: 'none' }} onChange={(event) => handleImageSelection(event, 'camera')} />
      <input ref={galleryInputRef} type="file" accept="image/*,video/*" data-testid={`${dataTestIdPrefix}-gallery-input`} style={{ display: 'none' }} onChange={(event) => handleImageSelection(event, 'gallery')} />

      <div style={{ width: '100%', display: 'flex', alignItems: isRecording ? 'center' : 'flex-end', gap: 10 }}>
        {isRecording ? (
          <button
            type="button"
            data-testid={`${dataTestIdPrefix}-cancel-recording-button`}
            onClick={handleCancelRecording}
            aria-label="Cancel voice recording"
            style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid rgba(214, 58, 85, 0.35)', background: 'rgba(214, 58, 85, 0.10)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'center', flexShrink: 0 }}
          >
            <Trash2 size={16} color="#D63A55" />
          </button>
        ) : (
          <button
            type="button"
            data-testid={`${dataTestIdPrefix}-camera-button`}
            onClick={() => cameraInputRef.current?.click()}
            disabled={isSubmitting || Boolean(editingMessage)}
            style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid var(--card-border)', background: 'var(--surface-2)', cursor: isSubmitting || editingMessage ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isSubmitting || editingMessage ? 0.45 : 1, alignSelf: 'center', flexShrink: 0 }}
          >
            <CameraActionIcon />
          </button>
        )}

        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', minHeight: 40, position: 'relative' }}>
          {isRecording ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              data-testid={`${dataTestIdPrefix}-recording-indicator`}
              style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 32, width: '100%' }}
            >
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#D63A55', boxShadow: '0 0 0 6px rgba(214, 58, 85, 0.14)' }} />
              <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text-2)', fontWeight: 700 }}>Recording voice message</span>
              <span style={{ marginLeft: 'auto', fontFamily: "'Inria Serif', serif", fontSize: 15, color: 'var(--text-2)' }}>{formatRecordingTime(recordingSeconds)}</span>
            </motion.div>
          ) : (
            <textarea
              ref={textareaRef}
              rows={1}
              value={value}
              onChange={(event) => {
                setLiveValue(event.target.value);
                onChange(event);
              }}
              onInput={(event) => {
                // Mirror the DOM value — covers IME paths that bypass
                // React's synthetic onChange (Greek word suggestions,
                // Chinese pinyin candidate taps, etc.).
                setLiveValue(event.target.value);
              }}
              onCompositionEnd={(event) => {
                // Final commit from the IME: flush the DOM value to
                // the parent so the Send button appears and the sent
                // payload is complete.
                setLiveValue(event.target.value);
                onChange({ target: { value: event.target.value } });
              }}
              onKeyDown={(event) => {
                // Desktop: plain Enter sends, Shift+Enter inserts newline.
                // Cmd/Ctrl+Enter kept as a legacy fallback.
                if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
                  event.preventDefault();
                  handleTextSubmit();
                  return;
                }
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                  event.preventDefault();
                  handleTextSubmit();
                }
              }}
              placeholder={placeholder}
              autoCapitalize="sentences"
              autoCorrect="on"
              autoComplete="off"
              spellCheck
              enterKeyHint="send"
              inputMode="text"
              // No `lang` attr: setting lang="auto" is NOT a valid BCP-47
              // tag and some Android IMEs read it to pick a dictionary,
              // then silently disable suggestions when they can't resolve
              // "auto" to a known language. Leaving the attribute off lets
              // GBoard / Samsung Keyboard / SwiftKey infer from the IME's
              // own active language setting, which is what the user
              // expects when they switch between English and Greek.
              data-testid={`${dataTestIdPrefix}-input`}
              className="messenger-composer-textarea"
              style={{
                flex: 1,
                width: '100%',
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontFamily: "'Inria Serif', serif",
                fontSize: 16,
                lineHeight: '22px',
                color: 'var(--text)',
                resize: 'none',
                minHeight: 24,
                maxHeight: 66,
                padding: '8px 0 6px',
              }}
            />
          )}
        </div>

        <div style={{ flexShrink: 0, alignSelf: isRecording ? 'center' : 'flex-end', paddingBottom: isRecording ? 0 : 1 }}>
          <AnimatePresence initial={false} mode="popLayout">
            {hasTypedText ? (
              <motion.button
                key="send-button"
                type="button"
                data-testid={`${dataTestIdPrefix}-send-button`}
                // Prevent focus-steal from the textarea on tap —
                // Android dismisses the soft keyboard the moment the
                // active element changes. `preventDefault` on
                // pointerdown works for both mouse and touch via the
                // Pointer Events spec, and is honoured by Capacitor's
                // WebView (unlike the older touchstart path which
                // double-fired and caused the keyboard to flicker).
                onPointerDown={(event) => {
                  event.preventDefault();
                }}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => { if (!isSubmitting) handleTextSubmit(); }}
                // IMPORTANT: do NOT set `disabled` while submitting —
                // Android blurs focused elements the instant they
                // disable, which closes the keyboard. Guard inside the
                // handler instead.
                initial={{ opacity: 0, scale: 0.82, x: 10 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.82, x: 10 }}
                transition={actionAnimation}
                style={{ width: 42, height: 42, borderRadius: '50%', border: 'none', cursor: isSubmitting ? 'default' : 'pointer', background: 'var(--accent-purple)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--premium-shadow-main)', opacity: isSubmitting ? 0.65 : 1 }}
              >
                <Send size={18} color="#FFFFFF" />
              </motion.button>
            ) : isRecording ? (
              <motion.button
                key="stop-recording"
                type="button"
                data-testid={`${dataTestIdPrefix}-stop-recording-button`}
                onClick={handleMicToggle}
                initial={{ opacity: 0, scale: 0.82, x: 10 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.82, x: 10 }}
                transition={actionAnimation}
                style={{ width: 42, height: 42, borderRadius: '50%', border: 'none', cursor: 'pointer', background: '#D63A55', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 18px rgba(214, 58, 85, 0.24)' }}
              >
                <Square size={16} color="#FFFFFF" fill="#FFFFFF" />
              </motion.button>
            ) : (
              <motion.div key="action-buttons" layout transition={actionAnimation} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <motion.button
                  type="button"
                  data-testid={`${dataTestIdPrefix}-image-button`}
                  onClick={() => galleryInputRef.current?.click()}
                  disabled={isSubmitting || Boolean(editingMessage)}
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={actionAnimation}
                  style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid var(--card-border)', background: 'var(--surface-2)', cursor: isSubmitting || editingMessage ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isSubmitting || editingMessage ? 0.45 : 1 }}
                >
                  <ImageActionIcon />
                </motion.button>
                <motion.button
                  type="button"
                  data-testid={`${dataTestIdPrefix}-mic-button`}
                  onClick={handleMicToggle}
                  disabled={isSubmitting || Boolean(editingMessage)}
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={actionAnimation}
                  style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid var(--card-border)', background: 'var(--surface-2)', cursor: isSubmitting || editingMessage ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isSubmitting || editingMessage ? 0.45 : 1 }}
                >
                  <MicActionIcon />
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};